import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, ExternalLink, Images, Type } from "lucide-react";
import { normalizeFontFamilyName, parseTypographyStyleClaim } from "@runcheetah/shared";

import { CustomTooltip } from "@/components/custom/custom-tooltip";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import { trpc } from "@/lib/trpc";

import {
  buildDisplayAssets,
  filterAssetsForGallery,
  formatBytes,
  formatImageClassificationLabel,
  getAssetFontDisplayName,
  getAssetNormalizedFontFamily,
  groupAssetsBySource,
  normalizeSourceUrl,
  parseLegacyTypographyFamilyNames,
  type AssetGalleryFilter,
  type DisplayAssetFields,
} from "./asset-gallery-helpers";
import type { BrandAsset } from "./types";

type DisplayAsset = DisplayAssetFields<BrandAsset>;

function AssetCard({
  asset,
  isTypographyUsed,
}: {
  asset: DisplayAsset;
  isTypographyUsed: boolean;
}) {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [broken, setBroken] = useState(false);

  const onLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setDims({ w: img.naturalWidth, h: img.naturalHeight });
  }, []);

  const pagePath = asset.pageUrl
    ? (() => {
        try {
          return new URL(asset.pageUrl).pathname;
        } catch {
          return asset.pageUrl;
        }
      })()
    : null;

  const safeFontName = asset.checksum ? `font-${asset.checksum.slice(0, 8)}` : "sans-serif";
  const fontDisplayName = getAssetFontDisplayName(asset);
  const imageClassificationLabel = formatImageClassificationLabel(asset.imageClassification);
  const instagramCaption = asset.instagramCaptions[0] ?? asset.instagramCaption;
  const displayedDims =
    dims ??
    (asset.width && asset.height
      ? {
          w: asset.width,
          h: asset.height,
        }
      : null);

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border bg-card shadow-xs transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-md">
      <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-muted/60">
        {asset.assetKind === "font" ? (
          <div className="group relative flex h-full w-full flex-col justify-between overflow-hidden bg-[linear-gradient(135deg,hsl(var(--background)),hsl(var(--muted)))] p-4">
            <style>
              {`
                @font-face {
                  font-family: '${safeFontName}';
                  src: url('${asset.signedUrl}');
                }
              `}
            </style>
            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              <span>Typeface</span>
              <Type className="size-3.5" />
            </div>
            <span
              className="text-5xl leading-none text-foreground"
              style={{ fontFamily: safeFontName }}
              title="AaBbCcDdEeFfGg"
            >
              Aa Bb
            </span>
            <div className="space-y-1">
              <p className="truncate text-sm font-semibold" title={fontDisplayName ?? undefined}>
                {fontDisplayName ?? "Unknown font"}
              </p>
              <p className="text-xs text-muted-foreground">The quick brown fox jumps.</p>
            </div>
          </div>
        ) : broken ? (
          <div className="flex flex-col items-center gap-1 p-4 text-xs text-muted-foreground">
            <Images className="size-6 opacity-40" />
            <span>Image unavailable</span>
          </div>
        ) : (
          <img
            src={asset.signedUrl}
            alt={asset.sourceUrl}
            onLoad={onLoad}
            onError={() => setBroken(true)}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="inline-flex w-fit items-center rounded-full border bg-background px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              {asset.contentType?.split("/")[1] ?? asset.assetKind}
            </span>
            {imageClassificationLabel && asset.assetKind === "image" ? (
              <span className="inline-flex w-fit items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                {imageClassificationLabel}
                {asset.imageClassificationConfidencePct !== null
                  ? ` (${asset.imageClassificationConfidencePct}%)`
                  : ""}
              </span>
            ) : null}
            {asset.assetKind === "font" ? (
              <span
                className="truncate text-sm font-semibold"
                title={fontDisplayName ?? "Unknown font family"}
              >
                {fontDisplayName ?? "Unknown font family"}
              </span>
            ) : null}
            {asset.assetKind === "font" ? (
              <span
                className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                  isTypographyUsed
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-muted-foreground/20 bg-muted/50 text-muted-foreground"
                }`}
              >
                {isTypographyUsed ? "Used in claim" : "Imported only"}
              </span>
            ) : null}
            {asset.occurrenceCount > 1 ? (
              <span className="inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                {asset.occurrenceCount} occurrences
              </span>
            ) : null}
            <div className="flex flex-wrap gap-1">
              {asset.sourceTypes.includes("website") ? (
                <span className="inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  Website
                </span>
              ) : null}
              {asset.sourceTypes.includes("instagram") ? (
                <span className="inline-flex w-fit items-center gap-1 rounded-full border border-pink-200 bg-pink-50 px-2 py-0.5 text-[10px] font-medium text-pink-700">
                  <Images className="size-3" />
                  Instagram
                </span>
              ) : null}
              {asset.referenceEnabled ? (
                <span className="inline-flex w-fit items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                  Reference enabled
                </span>
              ) : null}
            </div>
          </div>
          <span className="pt-1 font-mono text-xs tabular-nums text-muted-foreground">
            {formatBytes(asset.sizeBytes)}
          </span>
        </div>

        {displayedDims ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-muted-foreground">
            <span>
              {displayedDims.w} × {displayedDims.h}px
            </span>
            {asset.assetKind === "image" && asset.isVector ? <span>vector</span> : null}
          </div>
        ) : null}

        {asset.assetKind === "image" && asset.imageClassificationModel ? (
          <p
            className="truncate text-[11px] text-muted-foreground"
            title={asset.imageClassificationModel}
          >
            Classified by {asset.imageClassificationModel}
          </p>
        ) : null}

        {instagramCaption ? (
          <p
            className="line-clamp-2 text-[11px] leading-5 text-muted-foreground"
            title={instagramCaption}
          >
            {instagramCaption}
          </p>
        ) : null}

        {(pagePath && asset.pageUrl) || asset.instagramPermalink ? (
          <div className="flex w-full items-center gap-2 overflow-hidden">
            <a
              href={asset.instagramPermalink ?? asset.pageUrl ?? asset.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center gap-1 truncate font-mono text-xs text-muted-foreground hover:text-foreground"
              title={asset.instagramPermalink ?? asset.pageUrl ?? asset.sourceUrl}
            >
              <span className="truncate">
                {asset.instagramPermalink ? "Instagram post" : pagePath}
              </span>
              <ExternalLink className="size-3 shrink-0" />
            </a>

            <CustomTooltip content="Download Asset">
              <a
                href={asset.signedUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <Download className="size-3.5" />
              </a>
            </CustomTooltip>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function AssetsGallery({ brandId }: { brandId: string }) {
  const { data: assets, isPending, error } = trpc.admin.brands.assets.useQuery({ brandId });
  const { data: facts } = trpc.admin.brands.facts.useQuery({ brandId });
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<AssetGalleryFilter>("images");
  const displayAssets = useMemo(() => buildDisplayAssets(assets ?? []), [assets]);
  const assetCounts = useMemo(
    () => ({
      images: filterAssetsForGallery(displayAssets, "images").length,
      referentiable: filterAssetsForGallery(displayAssets, "referentiable").length,
      fonts: filterAssetsForGallery(displayAssets, "fonts").length,
    }),
    [displayAssets],
  );
  const filteredAssets = useMemo(
    () => filterAssetsForGallery(displayAssets, filter),
    [displayAssets, filter],
  );
  const groupedFilteredAssets = useMemo(
    () => groupAssetsBySource(filteredAssets),
    [filteredAssets],
  );

  useEffect(() => {
    setPage(0);
  }, [filter, displayAssets.length]);

  const typographyUsage = useMemo(() => {
    const families = new Set<string>();
    const sourceUrls = new Set<string>();
    const objectKeys = new Set<string>();

    for (const fact of facts ?? []) {
      if (fact.fieldPath !== "visualIdentitySnapshot.typographyStyle") {
        continue;
      }

      const claim = parseTypographyStyleClaim(fact.valueJson);
      if (!claim) {
        if (typeof fact.valueJson === "string") {
          for (const family of parseLegacyTypographyFamilyNames(fact.valueJson)) {
            families.add(normalizeFontFamilyName(family));
          }
        }
        continue;
      }

      for (const family of claim.families) {
        families.add(family.normalizedFamily ?? normalizeFontFamilyName(family.family));
        for (const sourceUrl of family.sourceUrls) {
          sourceUrls.add(normalizeSourceUrl(sourceUrl));
        }
        for (const objectKey of family.assetObjectKeys) {
          objectKeys.add(objectKey);
        }
      }
    }

    return { families, sourceUrls, objectKeys };
  }, [facts]);

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Assets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-md" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Assets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-24 items-center justify-center rounded-md border text-sm text-destructive">
            Failed to load assets
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalPages = Math.ceil(filteredAssets.length / PAGE_SIZE);
  const pageAssets = filteredAssets.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pageAssetIds = new Set(pageAssets.map((asset) => asset.id));
  const galleryFilters: Array<{ key: AssetGalleryFilter; label: string; count: number }> = [
    { key: "images", label: "Images", count: assetCounts.images },
    { key: "referentiable", label: "Referentiable", count: assetCounts.referentiable },
    { key: "fonts", label: "Fonts", count: assetCounts.fonts },
  ];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="gap-4 border-b bg-muted/20">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Images className="size-4 text-muted-foreground" />
              Brand Asset Library
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Review canonical images and type assets discovered during ingestion.
            </p>
          </div>
          <div className="inline-flex w-fit rounded-full border bg-background p-1 shadow-xs">
            {galleryFilters.map((item) => (
              <Button
                key={item.key}
                type="button"
                variant={filter === item.key ? "default" : "ghost"}
                size="sm"
                className="h-8 rounded-full px-3 text-xs"
                onClick={() => setFilter(item.key)}
              >
                {item.label}
                <span className="ml-1.5 rounded-full bg-background/70 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {item.count}
                </span>
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 p-5">
        {filteredAssets.length > 0 ? (
          <>
            <div className="space-y-7">
              {groupedFilteredAssets.map((group) => {
                const assetsForPage = group.assets.filter((asset) => pageAssetIds.has(asset.id));
                if (assetsForPage.length === 0) {
                  return null;
                }

                return (
                  <section key={group.key} className="space-y-3">
                    <div className="flex items-center justify-between gap-3 border-b pb-2">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">{group.title}</h3>
                        <p className="text-xs text-muted-foreground">{group.description}</p>
                      </div>
                      <span className="rounded-full border bg-background px-2 py-0.5 text-xs text-muted-foreground">
                        {group.assets.length}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                      {assetsForPage.map((asset) => {
                        const normalizedFamily = getAssetNormalizedFontFamily(asset);
                        const hasMatchingSourceUrl = asset.sourceUrls.some((sourceUrl) =>
                          typographyUsage.sourceUrls.has(normalizeSourceUrl(sourceUrl)),
                        );

                        const isTypographyUsed =
                          asset.assetKind === "font" &&
                          (typographyUsage.objectKeys.has(asset.objectKey) ||
                            hasMatchingSourceUrl ||
                            (normalizedFamily !== null &&
                              typographyUsage.families.has(normalizedFamily)));

                        return (
                          <AssetCard
                            key={asset.id}
                            asset={asset}
                            isTypographyUsed={isTypographyUsed}
                          />
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
            {totalPages > 1 ? (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed bg-muted/20 text-center text-sm text-muted-foreground">
            <Images className="size-6 opacity-50" />
            <span>
              {displayAssets.length === 0
                ? "No assets found for this brand yet."
                : `No ${filter === "referentiable" ? "referentiable images" : filter} found.`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
